/**
 * Double écran « Régie » (formule Pro) : ouvrir l'écran public dans une 2e fenêtre,
 * placée sur le projecteur quand le navigateur sait le faire.
 *
 * - Chrome / Edge (API de gestion des fenêtres) : la fenêtre s'ouvre à la taille du
 *   2e écran, directement dessus. Le plein écran demande ensuite un double-clic
 *   dedans (aucun navigateur ne l'accorde sans geste dans la fenêtre elle-même).
 * - Safari / Firefox, ou autorisation refusée : la fenêtre s'ouvre à côté, on la
 *   glisse sur le projecteur.
 */

/** Paramètre d'URL qui fait de la page TV l'écran public. */
export const PUBLIC_DISPLAY_PARAM = 'display';
export const PUBLIC_DISPLAY_VALUE = 'public';

/** Nom de la fenêtre : rouvrir l'écran public réutilise la même fenêtre au lieu d'en empiler. */
const PUBLIC_WINDOW_NAME = 'unboared-ecran-public';

const REMEMBER_KEY = 'ub_dual_screen';

export function publicScreenURL(clientID: string, gameID: string) {
    const q = new URLSearchParams({ game: gameID, [PUBLIC_DISPLAY_PARAM]: PUBLIC_DISPLAY_VALUE });
    return `/app/${encodeURIComponent(clientID)}?${q.toString()}`;
}

export const regieURL = (clientID: string) => `/animator/${encodeURIComponent(clientID)}/regie`;

/* --- API de gestion des fenêtres (Chrome/Edge 100+, absente de lib.dom) --------- */

type ScreenDetailed = Screen & {
    availLeft: number;
    availTop: number;
    isPrimary: boolean;
    isInternal: boolean;
    label?: string;
};
type ScreenDetails = { screens: ScreenDetailed[]; currentScreen: ScreenDetailed };
type WindowWithScreens = Window & { getScreenDetails?: () => Promise<ScreenDetails> };

export type ScreenAccess = 'granted' | 'prompt' | 'denied' | 'unsupported';

const hasWindowManagement = () => typeof (window as WindowWithScreens).getScreenDetails === 'function';

/**
 * Un 2e écran est-il branché ? `true` / `false` sur Chrome et Edge (sans rien demander),
 * `null` ailleurs (on ne sait pas : Safari, Firefox).
 */
export function isExtendedDisplay(): boolean | null {
    const extended = (window.screen as Screen & { isExtended?: boolean }).isExtended;
    return typeof extended === 'boolean' ? extended : null;
}

export async function screenAccess(): Promise<ScreenAccess> {
    if (!hasWindowManagement()) return 'unsupported';
    try {
        const status = await navigator.permissions.query({ name: 'window-management' as PermissionName });
        return status.state as ScreenAccess;
    } catch {
        // Ancien nom de la permission (Chrome < 111), ou query non prise en charge.
        return 'prompt';
    }
}

/** Demande l'accès aux écrans (fenêtre d'autorisation du navigateur la 1re fois). */
export async function requestScreenAccess(): Promise<boolean> {
    if (!hasWindowManagement()) return false;
    try {
        await (window as WindowWithScreens).getScreenDetails!();
        return true;
    } catch {
        return false;
    }
}

/** L'écran sur lequel l'écran public doit s'ouvrir : un autre que celui de la régie, externe de préférence. */
async function targetScreen(): Promise<ScreenDetailed | null> {
    if (!hasWindowManagement()) return null;
    try {
        const details = await (window as WindowWithScreens).getScreenDetails!();
        const others = details.screens.filter((s) => s !== details.currentScreen);
        return others.find((s) => !s.isInternal) ?? others[0] ?? null;
    } catch {
        return null;
    }
}

/**
 * Cette page est-elle déjà sur le projecteur (écran externe, alors qu'un écran interne
 * existe) ? Ouvrir l'écran public « ailleurs » le mettrait sur le portable et laisserait
 * la régie devant la salle. Demande l'autorisation déjà accordée ; sinon `false`.
 */
export async function pageIsOnExternalScreen(): Promise<boolean> {
    if (!hasWindowManagement()) return false;
    try {
        const details = await (window as WindowWithScreens).getScreenDetails!();
        return details.currentScreen.isInternal === false && details.screens.some((s) => s.isInternal);
    } catch {
        return false;
    }
}

let publicWindow: Window | null = null;

export type OpenResult = { opened: boolean; placed: boolean };

/**
 * Ouvre (ou rouvre) l'écran public. À appeler DANS le clic de l'utilisateur : le
 * bloqueur de fenêtres refuse une ouverture qui arrive trop longtemps après.
 * `placed` = la fenêtre a été posée sur le 2e écran.
 */
export async function openPublicScreen(clientID: string, gameID: string, access: ScreenAccess): Promise<OpenResult> {
    const url = publicScreenURL(clientID, gameID);
    // Avec l'autorisation déjà donnée, lire les écrans est immédiat : le clic reste valable.
    const target = access === 'granted' ? await targetScreen() : null;
    const features = target
        ? `popup,left=${target.availLeft},top=${target.availTop},width=${target.availWidth},height=${target.availHeight}`
        : 'popup,width=1280,height=720';
    const win = window.open(url, PUBLIC_WINDOW_NAME, features);
    if (!win) return { opened: false, placed: false };
    publicWindow = win;
    try {
        win.focus();
    } catch {
        /* rien */
    }
    return { opened: true, placed: !!target };
}

/** L'écran public ouvert depuis cette page est-il encore là ? (inconnu après un rechargement de la régie) */
export function publicWindowState(): 'open' | 'closed' | 'unknown' {
    if (!publicWindow) return 'unknown';
    return publicWindow.closed ? 'closed' : 'open';
}

export function closePublicScreen() {
    try {
        publicWindow?.close();
    } catch {
        /* rien */
    }
    publicWindow = null;
}

/* --- Se souvenir du choix (proposé à la soirée suivante) ------------------------ */

export function rememberDualScreen(clientID: string) {
    try {
        localStorage.setItem(REMEMBER_KEY, clientID);
    } catch {
        /* stockage indisponible : on ne proposera simplement pas de reprendre */
    }
}

export function forgetDualScreen() {
    try {
        localStorage.removeItem(REMEMBER_KEY);
    } catch {
        /* rien */
    }
}

export function usedDualScreenBefore(clientID: string) {
    try {
        return localStorage.getItem(REMEMBER_KEY) === clientID;
    } catch {
        return false;
    }
}
